import React, { useState } from 'react';
import { Field, Form, Formik } from 'formik';
import Modal from '../../../CommonComponents/Modal';
import {
  AddUserActivityBody,
  AddUserActivityFooter,
  AddUserActivityFormRow,
  AddUserActivityHelperText,
  AddUserActivityLabel,
  AddUserActivityTitle,
  StyledAddUserActivity,
} from './AddUserActivityStyles';
import { PrimaryButton, SecondaryButton } from '../../../CommonComponents/Button';
import FormikTextarea from '../../../CommonComponents/FormikTextarea';
import FormikReactSelect from '../../../CommonComponents/FormikReactSelect';
import { ActivityDateTimePicker } from '../ActivityDateTimePicker/ActivityDateTimePicker';
import FormikInput from '../../../CommonComponents/FormikInput';
import {
  activityTypeOptions,
  MAX_ACTIVITY_NAME_CHARS,
  MAX_COMMENT_CHARS,
} from './AddUserActivityConstants';
import FormikError from '../../../CommonComponents/FormikError';
import validationSchema from './AddUserActivityValidation';
import { useCreateUserActivity } from '../../api/useCreateUserActivity';
import ConfirmationModal from '../../../CommonComponents/ConfirmationModal/ConfirmationModalView';
import { Alert } from '../../../CommonComponents/Alert/Alert';
import Spinner from '../../../CommonComponents/Spinner';
import { ActivityType, CreateUserActivityDto } from '../../UserActivityTypes';

interface FormValues {
  date: string;
  activityType: ActivityType;
  activityName: string;
  comment: string;
}

interface Props {
  clientId: string;
  onRequestClose(): void;
}

export const AddUserActivity: React.FC<Props> = ({ clientId, onRequestClose }) => {
  const [newActivity, setNewActivity] = useState<FormValues | null>(null);

  const { mutateAsync: createUserActivity, isLoading, error } = useCreateUserActivity();

  return (
    <>
      <Modal
        isOpen
        onRequestClose={onRequestClose}
        showCloseModalCross
        shouldCloseOnOverlayClick={false}
      >
        <Formik
          initialValues={{
            date: '',
            activityType: ActivityType.DeliveryOfPlan,
            activityName: '',
            comment: '',
          }}
          validationSchema={validationSchema}
          onSubmit={values => {
            setNewActivity(values);
          }}
        >
          {({ values }) => (
            <Form>
              <StyledAddUserActivity>
                <AddUserActivityTitle>Add Activity</AddUserActivityTitle>

                {error && <Alert message={error.message} />}

                {isLoading && <Spinner />}

                <AddUserActivityBody>
                  <AddUserActivityFormRow>
                    <Field name="date" component={ActivityDateTimePicker} />
                    <FormikError name="date" isHidden />
                  </AddUserActivityFormRow>

                  <AddUserActivityFormRow>
                    <AddUserActivityLabel>Activity Type</AddUserActivityLabel>
                    <Field
                      useValue
                      name="activityType"
                      component={FormikReactSelect}
                      options={activityTypeOptions}
                    />
                  </AddUserActivityFormRow>

                  {values.activityType === ActivityType.OtherActivity && (
                    <AddUserActivityFormRow>
                      <AddUserActivityLabel>Name of the Activity</AddUserActivityLabel>
                      <Field
                        name="activityName"
                        placeholder=""
                        component={FormikInput}
                        maxLength={MAX_ACTIVITY_NAME_CHARS}
                      />
                      <FormikError name="activityName" isHidden />
                    </AddUserActivityFormRow>
                  )}

                  <AddUserActivityFormRow>
                    <AddUserActivityLabel>Comment</AddUserActivityLabel>
                    <Field
                      name="comment"
                      placeholder="Optional"
                      component={FormikTextarea}
                      maxLength={MAX_COMMENT_CHARS}
                      rows={5}
                    />
                    <AddUserActivityHelperText>
                      {MAX_COMMENT_CHARS - values.comment.length} characters remaining
                    </AddUserActivityHelperText>
                  </AddUserActivityFormRow>
                </AddUserActivityBody>

                <AddUserActivityFooter>
                  <SecondaryButton onClick={onRequestClose}>Cancel</SecondaryButton>
                  <PrimaryButton type="submit">Add Activity</PrimaryButton>
                </AddUserActivityFooter>
              </StyledAddUserActivity>
            </Form>
          )}
        </Formik>
      </Modal>

      {!!newActivity && (
        <ConfirmationModal
          isOpen
          onClose={() => setNewActivity(null)}
          title="Please note, once this activity is added, the activity cannot be edited or deleted."
          message="By clicking Confirm, you acknowledge this activity is correct and as intended."
          actions={
            <>
              <SecondaryButton onClick={() => setNewActivity(null)}>Cancel</SecondaryButton>
              <PrimaryButton
                onClick={async () => {
                  try {
                    const { activityName, activityType, date, comment } = newActivity;

                    const activity: CreateUserActivityDto = {
                      ActivityName: activityName,
                      ActivityType: activityType,
                      Date: date,
                      Comment: comment,
                    };

                    setNewActivity(null);

                    await createUserActivity({ clientId, activity });

                    onRequestClose();
                  } catch {
                    // Ignore
                  }
                }}
              >
                Confirm
              </PrimaryButton>
            </>
          }
        />
      )}
    </>
  );
};
